import React, { Dispatch, SetStateAction, useCallback, useContext, useMemo } from 'react';

import { RowSelectionState } from '@tanstack/react-table';

import BidAccordionCards from './BidAccordionCard';
import { ListingWithBids } from './BidsTab';
import { getDefaultColumns } from './bidsTable.utils';
import { restrictedResizeColumnIds } from '../../../../app-level/config/TableRestrictions';
import { DataContext } from '../../../../context/DataContext';
import { useColumnOrder } from '../../../../hooks/useColumnOrder';
import { DataDisplayTable, WithSubData } from '../../../../ui-kit';

type ExpandedRowProps = {
  row: {
    original: ListingWithBids;
  };
};

interface BidsTableProps {
  data: ListingWithBids[];
  selectedListings: RowSelectionState;
  setSelectedListings: Dispatch<SetStateAction<RowSelectionState>>;
}
const BidsTable = ({ data, selectedListings, setSelectedListings }: BidsTableProps) => {
  const { state } = useContext(DataContext);
  const { setColumnOrder } = useColumnOrder(
    'bidsTable',
    state.globalTableOrderConfig.marketPlace.activeTrades.bidsTable as string[],
  );

  const columnsToUse = useMemo(
    () =>
      getDefaultColumns(state.globalTableOrderConfig.marketPlace.activeTrades.bidsTable).filter(
        Boolean,
      ),
    [state.globalTableOrderConfig.marketPlace.activeTrades.bidsTable],
  );

  const tableData: WithSubData<ListingWithBids>[] = data.map((deal) => {
    return {
      ...(deal as ListingWithBids),
      last_updated_date: '',
      subData: [],
      __typename: '',
    } satisfies WithSubData<ListingWithBids>;
  });

  const getRowId = useCallback((row: any) => {
    return (row as WithSubData<ListingWithBids>)?.listing?.id;
  }, []);

  return (
    <DataDisplayTable
      getRowId={getRowId}
      data={tableData}
      // @ts-expect-error - DataDisplayTable React.memo wrapper doesn't preserve generic types
      columns={columnsToUse}
      noDataMessage={'No Data Found'}
      selection={{
        selected: selectedListings,
        onSelectionChange: setSelectedListings,
      }}
      renderExpandedRow={(row) => <ExpandedRow row={row as any} />}
      isSelectionInfoIcon={false}
      thClasses="bg-slate-950"
      tableClasses="border-separate [border-spacing:0_8px] bg-slate-950"
      stylingOptions={{
        rowColor: 'bg-gray-950',
        alternateLightRows: false,
        hoverRowColor: 'gray',
        selectedRowColor: 'bg-gray-1000',
      }}
      rowFirstColumnClasses="border-l-4 border-pink-500"
      rowFirstHeaderClasses="border-l-4 border-transparent-0"
      pillBtnVariant={'muted_light'}
      downloadCSV={false}
      isDraggable={true}
      resizeCfg={restrictedResizeColumnIds.marketPlace.activeTrades.bidsTable}
      setColumnOrder={setColumnOrder}
      columnOrder={state.globalTableOrderConfig.marketPlace.activeTrades.bidsTable}
      columnPinningLeft={['selection', 'icons']}
    />
  );
};

export default BidsTable;

const ExpandedRow = ({ row }: ExpandedRowProps) => {
  const listing = row.original || null;

  return (
    <div className="w-full">
      {listing.deals.length > 0 && <BidAccordionCards listing={listing} />}
    </div>
  );
};
